
import * as THREE from "../../libs/three.js/build/three.module.js";
import {profileSegments, IfcOverlay, BaseMapOverlay, GisOverlay} from "../modules/ProfileOverlays/ProfileOverlays.js";
import {ProfileTools} from "../modules/ProfileOverlays/ProfileTools.js";
import {Utils} from "../utils.js";
import {Points} from "../Points.js";
import {CSVExporter} from "../exporter/CSVExporter.js";
import {LASExporter} from "../exporter/LASExporter.js";
import { EventDispatcher } from "../EventDispatcher.js";
import {PointCloudTree} from "../PointCloudTree.js";
import {ProfileHover} from "./ProfileHover.js";
import {axisPositionAtMileage, profileLength, localizeNumberText} from "./ProfileHoverMath.js";
import {Renderer} from "../PotreeRenderer.js";
import {PointCloudMaterial} from "../materials/PointCloudMaterial.js";
import {PointSizeType} from "../defines.js";


function copyMaterial(source, target){

	for(let name of Object.keys(target.uniforms)){
		target.uniforms[name].value = source.uniforms[name].value;
	}

	target.gradientTexture = source.gradientTexture;
	target.visibleNodesTexture = source.visibleNodesTexture;
	target.classificationTexture = source.classificationTexture;
	target.matcapTexture = source.matcapTexture;

	target.activeAttributeName = source.activeAttributeName;
	target.ranges = source.ranges;

	//target.updateShaderSource();
}


class Batch{

	constructor(geometry, material){
		this.geometry = geometry;
		this.material = material;

		this.sceneNode = new THREE.Points(geometry, material);

		this.geometryNode = {
			estimatedSpacing: 1.0,
			geometry: geometry,
		};
	}

	getLevel(){
		return 0;
	}

}

class ProfileFakeOctree extends PointCloudTree{

	constructor(octree){
		super();

		this.trueOctree = octree;
		this.pcoGeometry = octree.pcoGeometry;
		this.points = [];
		this.visibleNodes = [];
		
		//this.material = this.trueOctree.material;
		this.material = new PointCloudMaterial();
		//this.material.copy(this.trueOctree.material);
		copyMaterial(this.trueOctree.material, this.material);
		this.material.pointSizeType = PointSizeType.FIXED;

		this.batchSize = 100 * 1000;
		this.currentBatch = null
	}

	getAttribute(name){
		return this.trueOctree.getAttribute(name);
	}

	dispose(){
		for(let node of this.visibleNodes){
			node.geometry.dispose();
		}

		this.visibleNodes = [];
		this.currentBatch = null;
		this.points = [];
	}

	addPoints(data){
		// since each call to addPoints can deliver very very few points,
		// we're going to batch them into larger buffers for efficiency.

		if(this.currentBatch === null){
			this.currentBatch = this.createNewBatch(data);
		}

		this.points.push(data);


		let updateRange = {
			start: this.currentBatch.geometry.drawRange.count,
			count: 0
		};
		let projectedBox = new THREE.Box3();

		let truePos = new THREE.Vector3();

		for(let i = 0; i < data.numPoints; i++){

			if(updateRange.start + updateRange.count >= this.batchSize){
				// current batch full, start new batch

				for(let key of Object.keys(this.currentBatch.geometry.attributes)){
					let attribute = this.currentBatch.geometry.attributes[key];
					attribute.updateRange.offset = updateRange.start;
					attribute.updateRange.count = updateRange.count;
					attribute.needsUpdate = true;
				}

				this.currentBatch.geometry.computeBoundingBox();
				this.currentBatch.geometry.computeBoundingSphere();

				this.currentBatch = this.createNewBatch(data);
				updateRange = {
					start: 0,
					count: 0
				};
			}

			truePos.set(
				data.data.position[3 * i + 0] + this.trueOctree.position.x,
				data.data.position[3 * i + 1] + this.trueOctree.position.y,
				data.data.position[3 * i + 2] + this.trueOctree.position.z,
			);

			let x = data.data.mileage[i];
			let y = 0;
			let z = truePos.z;

			projectedBox.expandByPoint(new THREE.Vector3(x, y, z));

			let index = updateRange.start + updateRange.count;
			let geometry = this.currentBatch.geometry;

			for(let attributeName of Object.keys(data.data)){
				let source = data.data[attributeName];
				let target = geometry.attributes[attributeName];
				let numElements = target.itemSize;
				
				for(let item = 0; item < numElements; item++){
					target.array[numElements * index + item] = source[numElements * i + item];
				}
			}

			{
				let position = geometry.attributes.position;

				position.array[3 * index + 0] = x;
				position.array[3 * index + 1] = y;
				position.array[3 * index + 2] = z;
			}

			updateRange.count++;
			this.currentBatch.geometry.drawRange.count++;
		}

		for(let key of Object.keys(this.currentBatch.geometry.attributes)){
			let attribute = this.currentBatch.geometry.attributes[key];
			attribute.updateRange.offset = updateRange.start;
			attribute.updateRange.count = updateRange.count;
			attribute.needsUpdate = true;
		}

		data.projectedBox = projectedBox;

		this.projectedBox = this.points.reduce( (a, i) => a.union(i.projectedBox), new THREE.Box3());
	}

	createNewBatch(data){
		let geometry = new THREE.BufferGeometry();

		// create new batches with batch_size elements of the same type as the attribute
		for(let attributeName of Object.keys(data.data)){
			let buffer = data.data[attributeName];
			let numElements = buffer.length / data.numPoints; // 3 for pos, 4 for col, 1 for scalars
			let constructor = buffer.constructor;
			let normalized = false;
			
			if(this.trueOctree.root.sceneNode){
				if(this.trueOctree.root.sceneNode.geometry.attributes[attributeName]){
					normalized = this.trueOctree.root.sceneNode.geometry.attributes[attributeName].normalized;
				}
			}
			

			let batchBuffer = new constructor(numElements * this.batchSize);

			let bufferAttribute = new THREE.BufferAttribute(batchBuffer, numElements, normalized);
			bufferAttribute.potree = {
				range: [0, 1],
			};

			geometry.setAttribute(attributeName, bufferAttribute);
		}

		geometry.drawRange.start = 0;
		geometry.drawRange.count = 0;

		let batch = new Batch(geometry, this.material);

		this.visibleNodes.push(batch);

		return batch;
	}
	
	computeVisibilityTextureData(){
		let data = new Uint8Array(this.visibleNodes.length * 4);
		let offsets = new Map();

		for(let i = 0; i < this.visibleNodes.length; i++){
			let node = this.visibleNodes[i];

			offsets[node] = i;
		}


		return {
			data: data,
			offsets: offsets,
		};
	}

}

export class ProfileWindow extends EventDispatcher {
	constructor (viewer) {
		super();

		this.viewer = viewer;
		this.elRoot = $('#profile_window');
		this.renderArea = this.elRoot.find('#profileCanvasContainer');
		this.svg = d3.select('svg#profileSVG');
		this.mouseIsDown = false;

		this.projectedBox = new THREE.Box3();
		this.pointclouds = new Map();
		this.numPoints = 0;
		this.lastAddPointsTimestamp = undefined;

		this.mouse = new THREE.Vector2(0, 0);
		this.scale = new THREE.Vector3(1, 1, 1);

		this.autoFitEnabled = true; // completely disable/enable
		this.autoFit = false; // internal

		let cwIcon = `${exports.resourcePath}/icons/arrow_cw.svg`;
		$('#potree_profile_rotate_cw').attr('src', cwIcon);

		let ccwIcon = `${exports.resourcePath}/icons/arrow_ccw.svg`;
		$('#potree_profile_rotate_ccw').attr('src', ccwIcon);
		
		let forwardIcon = `${exports.resourcePath}/icons/arrow_up.svg`;
		$('#potree_profile_move_forward').attr('src', forwardIcon);

		let backwardIcon = `${exports.resourcePath}/icons/arrow_down.svg`;
		$('#potree_profile_move_backward').attr('src', backwardIcon);

		let csvIcon = `${exports.resourcePath}/icons/file_csv_2d.svg`;
		$('#potree_download_csv_icon').attr('src', csvIcon);

		let lasIcon = `${exports.resourcePath}/icons/file_las_3d.svg`;
		$('#potree_download_las_icon').attr('src', lasIcon);

		let closeIcon = `${exports.resourcePath}/icons/close.svg`;
		$('#closeProfileContainer').attr("src", closeIcon);

		this.initTHREE();
		this.hover = new ProfileHover(viewer);
		this.initSVG();
		this.initListeners();

		this.pRenderer = new Renderer(this.renderer);

		// this.elRoot.i18n();
	}

	initListeners () {
		$(window).resize(() => {
			if (this.enabled) {
			this.render();
			}
		});

		// Profilen tegnes igen, når tegnefladen faktisk ændrer størrelse - også når vinduet vises igen efter at have været skjult (PointView
		// viser vinduet med Vue v-show uden at kalde show(), så `enabled` er ikke sat, og window.resize rammer ikke et skjult/resizet vindue).
		// Uden dette lå en profil, der blev beregnet mens vinduet var minimeret (areal 0 x 0), usynlig, til brugeren trykkede "Zoom til udstrækning".
		// Throttlet til én gang pr. frame, og springer over, mens fladen ikke har nogen størrelse eller samme størrelse som sidst.
		if (typeof ResizeObserver !== "undefined" && this.renderArea[0]) {
			let frame = null;
			let lastWidth = 0;
			let lastHeight = 0;
			this.resizeObserver = new ResizeObserver(() => {
				if (frame !== null) { return; }
				frame = requestAnimationFrame(() => {
					frame = null;
					const width = this.renderArea[0].clientWidth;
					const height = this.renderArea[0].clientHeight;
						if (!(width > 0 && height > 0)) {
						// skjult/minimeret/lukket: ingen markør i 3D og ingen kamerafølge, før vinduet vises igen
						lastWidth = 0;
						lastHeight = 0;
						this.clearHover();
						this.hover.reset();
						return;
					}
					if (width === lastWidth && height === lastHeight) { return; }
					lastWidth = width;
					lastHeight = height;
					this.fitToProjectedBox();
					this.render();
				});
			});
			this.resizeObserver.observe(this.renderArea[0]);
		}

		this.renderArea.mousedown(e => {
			this.mouseIsDown = true;
		});

		this.renderArea.mouseup(e => {
			this.mouseIsDown = false;
		});

		// Musen behandles højst én gang pr. billede og kun med den SENESTE position (ingen kø af arbejde): hover-opslaget gennemløber
		// punkterne i de berørte noder, og ét museevent pr. millisekund (hurtig mus) gav før et opslag + to render-kald pr. event.
		this.pendingPointer = null;
		this.pointerFrame = null;
		this.lastHoverIndex = -1;
		this.lastHoverPoints = null;
		this.hoverActive = false;

		let processPointer = () => {
			this.pointerFrame = null;
			let pointer = this.pendingPointer;
			this.pendingPointer = null;

			if (!pointer || this.pointclouds.size === 0) {
				return;
			}

			let rect = this.renderArea[0].getBoundingClientRect();
			let x = pointer.clientX - rect.left;
			let y = pointer.clientY - rect.top;

			let newMouse = new THREE.Vector2(x, y);

			if (this.mouseIsDown) {
				// DRAG
				this.autoFit = false;
				this.lastDrag = new Date().getTime();

				let cPos = [this.scaleX.invert(this.mouse.x), this.scaleY.invert(this.mouse.y)];
				let ncPos = [this.scaleX.invert(newMouse.x), this.scaleY.invert(newMouse.y)];

				this.camera.position.x -= ncPos[0] - cPos[0];
				this.camera.position.z -= ncPos[1] - cPos[1];

				// under panorering peger musen ikke på et punkt: markøren i 3D skjules og kameraet står stille
				this.clearHover();
				this.render();
			} else {
				// FIND HOVERED POINT
				let radius = Math.abs(this.scaleX.invert(0) - this.scaleX.invert(40));
				let mileage = this.scaleX.invert(newMouse.x);
				let elevation = this.scaleY.invert(newMouse.y);

				let closest = this.selectPoint(mileage, elevation, radius);

				if (closest) {
					let point = closest.point;

					let position = new Float64Array([
						point.position[0] + closest.pointcloud.position.x,
						point.position[1] + closest.pointcloud.position.y,
						point.position[2] + closest.pointcloud.position.z
					]);

					this.hoverActive = true;
					this.pickSphere.visible = true;
					this.pickSphere.scale.set(0.5 * radius, 0.5 * radius, 0.5 * radius);
					this.pickSphere.position.set(point.mileage, 0, position[2]);

					// samme punkt som sidst: ingen ny info-tabel og ingen ny markørposition
					let changed = closest.index !== this.lastHoverIndex || closest.points !== this.lastHoverPoints;
					this.lastHoverIndex = closest.index;
					this.lastHoverPoints = closest.points;

					if (changed) {
						this.selectedPoint = point;
						this.showHoverMarker(point.mileage, position[2]);
						this.updateSelectionInfo(closest, position);
					}
				} else {
					// ingen gyldige punkter ved musen: ingen markør, kameraet står stille
					this.clearHover();
				}
				this.render();
			}

			this.mouse.copy(newMouse);
		};
		this.flushPointer = () => {
			if (this.pointerFrame !== null) {
				cancelAnimationFrame(this.pointerFrame);
				processPointer();
			}
		};

		this.renderArea.mousemove(e => {
			if (this.pointclouds.size === 0) {
				return;
			}
			this.pendingPointer = {clientX: e.clientX, clientY: e.clientY};
			if (this.pointerFrame === null) {
				this.pointerFrame = requestAnimationFrame(processPointer);
			}
		});

		this.renderArea.mouseenter(() => {
			this.hover.enter();
		});

		// musen forlader tegnefladen: ingen afventende position, ingen markør (hverken i profilen eller i 3D), kameraet står stille
		this.renderArea.mouseleave(() => {
			if (this.pointerFrame !== null) {
				cancelAnimationFrame(this.pointerFrame);
				this.pointerFrame = null;
			}
			this.pendingPointer = null;
			this.clearHover();
			if (this.enabled !== false) {
				this.render();
			}
		});

		// slippes museknappen uden for tegnefladen, forbliver panoreringen ikke "hængende"
		$(document).on("mouseup", () => {
			this.mouseIsDown = false;
		});

		let onWheel = e => {
			this.autoFit = false;
			this.flushPointer();

			let delta = 0;
			if (e.wheelDelta !== undefined) { // WebKit / Opera / Explorer 9
				delta = e.wheelDelta;
			} else if (e.detail !== undefined) { // Firefox
				delta = -e.detail;
			}

			let ndelta = Math.sign(delta);

			let cPos = [this.scaleX.invert(this.mouse.x), this.scaleY.invert(this.mouse.y)];

			if (ndelta > 0) {
				// + 10%
				this.scale.multiplyScalar(1.1);
			} else {
				// - 10%
				this.scale.multiplyScalar(100 / 110);
			}

			this.updateScales();
			let ncPos = [this.scaleX.invert(this.mouse.x), this.scaleY.invert(this.mouse.y)];

			this.camera.position.x -= ncPos[0] - cPos[0];
			this.camera.position.z -= ncPos[1] - cPos[1];

			this.render();
			this.updateScales();
		};
		$(this.renderArea)[0].addEventListener('mousewheel', onWheel, false);
		$(this.renderArea)[0].addEventListener('DOMMouseScroll', onWheel, false); // Firefox

		$('#closeProfileContainer').click(() => {
			this.hide();
		});

		let getProfilePoints = () => {
			let points = new Points();
			
			for(let [pointcloud, entry] of this.pointclouds){
				for(let pointSet of entry.points){

					let originPos = pointSet.data.position;
					let trueElevationPosition = new Float32Array(originPos);
					for(let i = 0; i < pointSet.numPoints; i++){
						trueElevationPosition[3 * i + 2] += pointcloud.position.z;
					}

					pointSet.data.position = trueElevationPosition;
					points.add(pointSet);
					pointSet.data.position = originPos;
				}
			}

			return points;
		};

		$('#potree_download_csv_icon').click(() => {
			
			let points = getProfilePoints();

			let string = CSVExporter.toString(points);

			let blob = new Blob([string], {type: "text/string"});
			$('#potree_download_profile_ortho_link').attr('href', URL.createObjectURL(blob));
		});

		$('#potree_download_las_icon').click(() => {

			let points = getProfilePoints();

			let buffer = LASExporter.toLAS(points);

			let blob = new Blob([buffer], {type: "application/octet-binary"});
			$('#potree_download_profile_link').attr('href', URL.createObjectURL(blob));
		});
	}

	// Profilens segmenter (til markørens akseposition) - genberegnes først, når profilen ændres (reset() nulstiller cachen)
	getProfileSegments () {
		let profile = this.viewer.profileWindowController && this.viewer.profileWindowController.profile;
		if (!profile) {
			return null;
		}
		if (!this.segmentCache || this.segmentCache.profile !== profile || this.segmentCache.count !== profile.points.length || this.segmentCache.version !== this.segmentVersion) {
			this.segmentCache = {
				profile,
				count: profile.points.length,
				version: this.segmentVersion,
				segments: profileSegments(profile.points)
			};
		}

		return this.segmentCache.segments;
	}

	// Markøren i 3D: profilaksens xy ved punktets mileage (klemt til profilens ender) og punktets kote - samme position som kamerafølgen bruger.
	showHoverMarker (mileage, z) {
		let segments = this.getProfileSegments();
		let axis = segments ? axisPositionAtMileage(segments, mileage) : null;
		if (!axis) {
			this.hover.hide();
			return;
		}

		let profile = this.viewer.profileWindowController.profile;
		this.hover.show({x: axis.x, y: axis.y, z: z}, {length: profileLength(segments), width: profile.width});
	}

	// Synkroniseret kamerafølge (valgfri, slået fra som standard): se ProfileHover.js / ProfileHoverMath.js
	setSyncEnabled (on) {
		this.hover.setSyncEnabled(on);
		this.dispatchEvent({type: 'sync_changed', enabled: this.hover.syncEnabled});
	}

	get syncEnabled () {
		return this.hover.syncEnabled;
	}

	// Ingen punkt under musen (mus væk, panorering, skjult/nulstillet vindue): hverken 2D-markør eller 3D-markør, og kameraet står stille.
	clearHover () {
		if (this.hoverActive) {
			this.selectedRows = null;
			this.dispatchEvent({type: 'point_selected', rows: null, position: null});
		}
		this.hoverActive = false;
		this.lastHoverIndex = -1;
		this.lastHoverPoints = null;
		this.pickSphere.visible = false;
		this.hover.hide();
	}

	// Rækkerne (attribut, tekst) for det valgte punkt - vises af profilens info-popup (frontend); sendes kun, når punktet er ændret.
	// Ingen DOM-skrivning her: popup'en ejes af brugerfladen og findes ikke altid.
	updateSelectionInfo (closest, position) {
		let point = closest.point;
		let rows = [];

		for (let attributeName of Object.keys(point)) {

			let value = point[attributeName];
			let attribute = closest.pointcloud.getAttribute(attributeName);

			let transform = value => value;
			if(attribute && attribute.type.size > 4){
				let range = attribute.initialRange;
				let scale = 1 / (range[1] - range[0]);
				let offset = range[0];
				transform = value => value / scale + offset;
			}

			if (attributeName === 'position') {
				let values = [...position].map(v => Utils.addCommas(v.toFixed(3)));
				rows.push({name: 'x', value: values[0]}, {name: 'y', value: values[1]}, {name: 'z', value: values[2]});
			} else if (attributeName === 'rgba') {
				rows.push({name: attributeName, value: Array.from(value).join(', ')});
			} else if (attributeName === 'normal') {
				continue;
			} else if (attributeName === 'mileage') {
				rows.push({name: attributeName, value: value.toFixed(3)});
			} else {
				rows.push({name: attributeName, value: String(transform(value))});
			}
		}

		this.selectedRows = rows;
		this.dispatchEvent({type: 'point_selected', rows: rows, position: [...position], mileage: point.mileage, elevation: position[2]});
	}

	selectPoint (mileage, elevation, radius) {
		let closest = {
			distance: Infinity,
			pointcloud: null,
			points: null,
			index: null
		};

		let pointBox = new THREE.Box2(
			new THREE.Vector2(mileage - radius, elevation - radius),
			new THREE.Vector2(mileage + radius, elevation + radius));

		let numTested = 0;
		let numSkipped = 0;
		let numTestedPoints = 0;
		let numSkippedPoints = 0;

		for (let [pointcloud, entry] of this.pointclouds) {
			for(let points of entry.points){

				let collisionBox = new THREE.Box2(
					new THREE.Vector2(points.projectedBox.min.x, points.projectedBox.min.z),
					new THREE.Vector2(points.projectedBox.max.x, points.projectedBox.max.z)
				);

				let intersects = collisionBox.intersectsBox(pointBox);

				if(!intersects){
					numSkipped++;
					numSkippedPoints += points.numPoints;
					continue;
				}

				numTested++;
				numTestedPoints += points.numPoints

				for (let i = 0; i < points.numPoints; i++) {

					let m = points.data.mileage[i] - mileage;
					let e = points.data.position[3 * i + 2] - elevation + pointcloud.position.z;
					let r = Math.sqrt(m * m + e * e);

					const withinDistance = r < radius && r < closest.distance;
					let unfilteredClass = true;

					if(points.data.classification){
						const classification = pointcloud.material.classification;

						const pointClassID = points.data.classification[i];
						const pointClassValue = classification[pointClassID];

						if(pointClassValue && (!pointClassValue.visible || pointClassValue.color.w === 0)){
							unfilteredClass = false;
						}
					}

					if (withinDistance && unfilteredClass) {
						closest = {
							distance: r,
							pointcloud: pointcloud,
							points: points,
							index: i
						};
					}
				}
			}
		}


		//console.log(`nodes: ${numTested}, ${numSkipped} || points: ${numTestedPoints}, ${numSkippedPoints}`);

		if (closest.distance < Infinity) {
			let points = closest.points;

			let point = {};

			let attributes = Object.keys(points.data);
			for (let attribute of attributes) {
				let attributeData = points.data[attribute];
				let itemSize = attributeData.length / points.numPoints;
				let value = attributeData.subarray(itemSize * closest.index, itemSize * closest.index + itemSize);

				if (value.length === 1) {
					point[attribute] = value[0];
				} else {
					point[attribute] = value;
				}
			}

			closest.point = point;

			return closest;
		} else {
			return null;
		}
	}

	initTHREE () {
		this.renderer = new THREE.WebGLRenderer({alpha: true, premultipliedAlpha: false});
		this.renderer.setClearColor(0x000000, 0);
		this.renderer.setSize(10, 10);
		this.renderer.autoClear = false;
		this.renderArea.append($(this.renderer.domElement));
		this.renderer.domElement.tabIndex = '2222';
		$(this.renderer.domElement).css('width', '100%');
		$(this.renderer.domElement).css('height', '100%');


		{
			let gl = this.renderer.getContext();

			if(gl.createVertexArray == null){
				let extVAO = gl.getExtension('OES_vertex_array_object');

				if(!extVAO){
					throw new Error("OES_vertex_array_object extension not supported");
				}

				gl.createVertexArray = extVAO.createVertexArrayOES.bind(extVAO);
				gl.bindVertexArray = extVAO.bindVertexArrayOES.bind(extVAO);
				gl.deleteVertexArray = extVAO.deleteVertexArrayOES.bind(extVAO);
			}
			
		}

		this.camera = new THREE.OrthographicCamera(-1000, 1000, 1000, -1000, -1000, 1000);
		this.camera.up.set(0, 0, 1);
		this.camera.rotation.order = "ZXY";
		this.camera.rotation.x = Math.PI / 2.0;
	

		this.scene = new THREE.Scene();
		this.profileScene = new THREE.Scene();

		let sg = new THREE.SphereGeometry(1, 16, 16);
		let sm = new THREE.MeshNormalMaterial();
		this.pickSphere = new THREE.Mesh(sg, sm);
		this.scene.add(this.pickSphere);
	}

	initSVG () {
		let width = this.renderArea[0].clientWidth;
		let height = this.renderArea[0].clientHeight;
		let marginLeft = this.renderArea[0].offsetLeft;
		let marginTop = this.renderArea[0].offsetTop;

		this.svg.selectAll('*').remove();

		this.scaleX = d3.scale.linear()
			.domain([this.camera.left + this.camera.position.x, this.camera.right + this.camera.position.x])
			.range([0, width]);
		this.scaleY = d3.scale.linear()
			.domain([this.camera.bottom + this.camera.position.z, this.camera.top + this.camera.position.z])
			.range([height, 0]);

		this.xAxis = d3.svg.axis()
			.scale(this.scaleX)
			.orient('bottom')
			.innerTickSize(-height)
			.outerTickSize(1)
			.tickPadding(10)
			.ticks(width / 70);

		this.yAxis = d3.svg.axis()
			.scale(this.scaleY)
			.orient('left')
			.innerTickSize(-width)
			.outerTickSize(1)
			.tickPadding(10)
			.ticks(height / 28);

		this.elXAxis = this.svg.append('g')
			.attr('class', 'x axis')
			.attr('transform', `translate(${marginLeft}, ${marginTop + height})`)
			.call(this.xAxis);

		this.elYAxis = this.svg.append('g')
			.attr('class', 'y axis')
			.attr('transform', `translate(${marginLeft}, ${marginTop})`)
			.call(this.yAxis);

		// aksetitler (tekst og enhed sættes af setAxisLabels; tomme indtil da)
		this.elXLabel = this.svg.append('text')
			.attr('class', 'axis-title axis-title-x')
			.attr('text-anchor', 'middle');
		this.elYLabel = this.svg.append('text')
			.attr('class', 'axis-title axis-title-y')
			.attr('text-anchor', 'middle');
		this.updateAxisLabels();
	}

	// Aksetitler med enhed: x = afstand langs profilen, y = kote. Teksten kommer fra brugerfladen (sprog); enheden udledes af viewerens
	// faktiske længdeenhed (samme enhed som profilens koordinater). decimal/group: talformat for akse-tallene (brugerens sprog).
	setAxisLabels ({x, y, decimal, group} = {}) {
		this.axisLabels = {
			x: x || '',
			y: y || '',
			decimal: decimal || '.',
			group: group || ','
		};
		this.updateAxisLabels();
		if (this.xAxis) {
			this.updateScales();
		}
	}

	updateAxisLabels () {
		if (!this.elXLabel) {
			return;
		}
		let labels = this.axisLabels || {x: '', y: ''};
		let unit = this.viewer.lengthUnit && this.viewer.lengthUnit.code ? this.viewer.lengthUnit.code : 'm';
		let width = this.renderArea[0].clientWidth;
		let height = this.renderArea[0].clientHeight;
		let left = this.renderArea[0].offsetLeft;
		let top = this.renderArea[0].offsetTop;

		this.elXLabel
			.attr('transform', `translate(${left + width / 2}, ${top + height + 40})`)
			.text(labels.x ? `${labels.x} (${unit})` : '');
		this.elYLabel
			.attr('transform', `translate(14, ${top + height / 2}) rotate(-90)`)
			.text(labels.y ? `${labels.y} (${unit})` : '');
	}

	// Akse-tal i brugerens talformat (d3's standardformat bruger punktum/komma som engelsk) med præcision efter tick-trinnet
	localTickFormat (scale, count) {
		let labels = this.axisLabels;
		let base = scale.tickFormat(count);
		if (!labels || (labels.decimal === '.' && labels.group === ',')) {
			return base;
		}

		return value => localizeNumberText(base(value), labels.group, labels.decimal);
	}

	fitToProjectedBox () {
		if (this.autoFit && this.autoFitEnabled && !this.projectedBox.isEmpty()) {
			let width = this.renderArea[0].clientWidth;
			let height = this.renderArea[0].clientHeight;
			// skjult/minimeret (ingen størrelse): scale ville blive 0 og kameraet ugyldigt. autoFit bliver stående, så profilen tilpasses,
			// når fladen får en størrelse igen (ResizeObserver i initListeners).
			if (!(width > 0 && height > 0)) { return; }

			let size = this.projectedBox.getSize(new THREE.Vector3());

			let sx = width / size.x;
			let sy = height / size.z;
			let scale = Math.min(sx, sy);
			// en helt flad/lodret eller enkeltpunkts-profil (udstrækning 0 i en retning) giver uendelig/NaN-skala: behold den nuværende
			if (!(scale > 0 && isFinite(scale))) { return; }

			let center = this.projectedBox.getCenter(new THREE.Vector3());
			this.scale.set(scale, scale, 1);
			this.camera.position.copy(center);
		}
	}

	// Ekstra linjer i profilens rum (x = mileage, z = kote), fx IFC-snit og terrænlinje.
	// positions: Float32Array med linjesegmenter (6 tal pr. segment) - se ProfileOverlays.js.
	setOverlay (id, positions, color, colors = null){
		if(!this.overlays){
			this.overlays = new Map();
		}

		let existing = this.overlays.get(id);
		if(existing){
			this.scene.remove(existing);
			existing.geometry.dispose();
			existing.material.dispose();
			this.overlays.delete(id);
		}

		if(positions && positions.length > 0){
			let geometry = new THREE.BufferGeometry();
			geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
			if(colors){
				geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
			}
			let material = new THREE.LineBasicMaterial({color: colors ? 0xffffff : color, vertexColors: !!colors, depthTest: false, depthWrite: false});
			let lines = new THREE.LineSegments(geometry, material);
			lines.frustumCulled = false;
			lines.renderOrder = 10;
			this.scene.add(lines);
			this.overlays.set(id, lines);

			// overlay-linjerne skal ogsaa med, naar profilen tilpasses vinduet (fx en profil
			// uden punkter i strimlen, kun terraen/IFC)
			geometry.computeBoundingBox();
			this.projectedBox.union(geometry.boundingBox);
			this.fitToProjectedBox();
		}

		// altid (som addPoints): vinduet kan vaere vist uden at show() er kaldt
		this.render();
	}

	clearOverlays (){
		if(this.overlays){
			for(let id of [...this.overlays.keys()]){
				this.setOverlay(id, null);
			}
		}
	}

	addPoints (pointcloud, points) {

		if(points.numPoints === 0){
			return;
		}

		let entry = this.pointclouds.get(pointcloud);
		if(!entry){
			entry = new ProfileFakeOctree(pointcloud);
			this.pointclouds.set(pointcloud, entry);
			this.profileScene.add(entry);

			let materialChanged = () => {
				// Fund #17-opfølgning 3 (2026-09-20): denne lytter forbliver
				// registreret for pointcloud'ets fulde levetid (kun fjernet
				// ved en fuld "on_reset_once") - IKKE kun mens profilvinduet
				// rent faktisk er åbent. Uden dette gate kørte render() (to
				// fulde render-kald + en clientWidth/clientHeight-læsning,
				// se render() nedenfor) her på HVER material-ændring -
				// inklusive opacity-slideren - selv når profilvinduet var
				// lukket og usynligt. Samme `this.enabled`-flag som
				// resize-lytteren i initListeners() allerede bruger til
				// samme formål. Sikkert at springe over: show() efterfølges
				// altid af setProfile() (se ProfilePanel.js), som selv
				// trigger et nyt render() via sin egen recompute-kæde -
				// vinduet fanger derfor altid op med den aktuelle
				// materialtilstand, når det åbnes igen.
				if (this.enabled) {
					this.render();
				}
			};

			materialChanged();

			pointcloud.material.addEventListener('material_property_changed', materialChanged);
			this.addEventListener("on_reset_once", () => {
				pointcloud.material.removeEventListener('material_property_changed', materialChanged);
			});
		}

		entry.addPoints(points);
		this.projectedBox.union(entry.projectedBox);

		this.fitToProjectedBox();

		//console.log(entry);

		this.render();

		let numPoints = 0;
		for (let [key, value] of this.pointclouds.entries()) {
			numPoints += value.points.reduce( (a, i) => a + i.numPoints, 0);
		}
		$(`#profile_num_points`).html(Utils.addCommas(numPoints));

	}

	reset () {
		this.lastReset = new Date().getTime();
		this.segmentVersion = (this.segmentVersion || 0) + 1;

		this.dispatchEvent({type: "on_reset_once"});
		this.removeEventListeners("on_reset_once");

		this.autoFit = true;
		this.projectedBox = new THREE.Box3();

		for(let [key, entry] of this.pointclouds){
			entry.dispose();
		}

		this.pointclouds.clear();
		this.mouseIsDown = false;
		this.mouse.set(0, 0);

		if(this.autoFitEnabled){
			this.scale.set(1, 1, 1);
		}
		this.clearHover();

		this.render();
	}

	show () {
		this.elRoot.fadeIn();
		this.enabled = true;
		this.dispatchEvent({type: "window_shown"});
	}

	hide () {
		this.elRoot.fadeOut();
		this.enabled = false;
	}

	updateScales () {

		let width = this.renderArea[0].clientWidth;
		let height = this.renderArea[0].clientHeight;

		let left = (-width / 2) / this.scale.x;
		let right = (+width / 2) / this.scale.x;
		let top = (+height / 2) / this.scale.y;
		let bottom = (-height / 2) / this.scale.y;

		this.camera.left = left;
		this.camera.right = right;
		this.camera.top = top;
		this.camera.bottom = bottom;
		this.camera.updateProjectionMatrix();

		this.scaleX.domain([this.camera.left + this.camera.position.x, this.camera.right + this.camera.position.x])
			.range([0, width]);
		this.scaleY.domain([this.camera.bottom + this.camera.position.z, this.camera.top + this.camera.position.z])
			.range([height, 0]);

		let marginLeft = this.renderArea[0].offsetLeft;
		let marginTop = this.renderArea[0].offsetTop;

		this.xAxis.scale(this.scaleX)
			.orient('bottom')
			.innerTickSize(-height)
			.outerTickSize(1)
			.tickPadding(10)
			.ticks(width / 70);
		this.yAxis.scale(this.scaleY)
			.orient('left')
			.innerTickSize(-width)
			.outerTickSize(1)
			.tickPadding(10)
			.ticks(height / 28);

		this.xAxis.tickFormat(this.localTickFormat(this.scaleX, width / 70));
		this.yAxis.tickFormat(this.localTickFormat(this.scaleY, height / 28));

		this.elXAxis
			.attr('transform', `translate(${marginLeft}, ${marginTop + height})`)
			.call(this.xAxis);
		this.elYAxis
			.attr('transform', `translate(${marginLeft}, ${marginTop})`)
			.call(this.yAxis);
		this.updateAxisLabels();
	}

	requestScaleUpdate(){

		let threshold = 100;
		let allowUpdate = ((this.lastReset === undefined) || (this.lastScaleUpdate === undefined)) 
			|| ((new Date().getTime() - this.lastReset) > threshold && (new Date().getTime() - this.lastScaleUpdate) > threshold);

		if(allowUpdate){

			this.updateScales();

			this.lastScaleUpdate = new Date().getTime();

			

			this.scaleUpdatePending = false;
		}else if(!this.scaleUpdatePending) {
			setTimeout(this.requestScaleUpdate.bind(this), 100);
			this.scaleUpdatePending = true;
		}
		
	}

	render () {
		let width = this.renderArea[0].clientWidth;
		let height = this.renderArea[0].clientHeight;

		let {renderer, pRenderer, camera, profileScene, scene} = this;
		let {scaleX, pickSphere} = this;

		renderer.setSize(width, height);

		renderer.setClearColor(0x000000, 0);
		renderer.clear(true, true, false);

		for(let pointcloud of this.pointclouds.keys()){
			let source = pointcloud.material;
			let target = this.pointclouds.get(pointcloud).material;
			
			copyMaterial(source, target);
			target.size = 2;
		}
		
		pRenderer.render(profileScene, camera, null);

		let radius = Math.abs(scaleX.invert(0) - scaleX.invert(5));

		if (radius === 0 || !this.hoverActive) {
			pickSphere.visible = false;
		} else {
			pickSphere.scale.set(radius, radius, radius);
			pickSphere.visible = true;
		}
		
		renderer.render(scene, camera);

		this.requestScaleUpdate();
		this.dispatchEvent({type: "rendered"});
	}
};

export class ProfileWindowController {
	constructor (viewer) {
		this.viewer = viewer;
		this.profileWindow = viewer.profileWindow;
		this.profile = null;
		this.numPoints = 0;
		this.threshold = 60 * 1000;
		this.rotateAmount = 10;

		this.scheduledRecomputeTime = null;

		this.enabled = true;

		this.requests = [];

		// ekstra linjer i profilen (IFC-snit, terrænlinje) - se ProfileOverlays.js
		this.overlaySources = [new IfcOverlay(viewer), new BaseMapOverlay(viewer), new GisOverlay(viewer)];
		this.overlayEnabled = {ifc: true, basemap: true, gis: true};
		this.overlayToken = 0;
		this.overlayTimer = null;
		this._refreshOverlays = () => { this.refreshOverlays(); };
		viewer.addEventListener("profile_overlays_dirty", this._refreshOverlays);
		this.profileWindow.addEventListener("window_shown", this._refreshOverlays);

		this._recompute = () => { this.recompute(); };

		// maaling og digitalisering i profilen (ProfileTools.js)
		this.profileWindow.tools = new ProfileTools(viewer, this.profileWindow, this);

		this.viewer.addEventListener("scene_changed", e => {
			e.oldScene.removeEventListener("pointcloud_added", this._recompute);
			e.scene.addEventListener("pointcloud_added", this._recompute);
		});
		this.viewer.scene.addEventListener("pointcloud_added", this._recompute);

		$("#potree_profile_rotate_amount").val(parseInt(this.rotateAmount));
		$("#potree_profile_rotate_amount").on("input", (e) => {
			const str = $("#potree_profile_rotate_amount").val();

			if(!isNaN(str)){
				const value = parseFloat(str);
				this.rotateAmount = value;
				$("#potree_profile_rotate_amount").css("background-color", "")
			}else{
				$("#potree_profile_rotate_amount").css("background-color", "#ff9999")
			}

		});

		const rotate = (radians) => {
			const profile = this.profile;
			const points = profile.points;
			const start = points[0];
			const end = points[points.length - 1];
			const center = start.clone().add(end).multiplyScalar(0.5);

			const mMoveOrigin = new THREE.Matrix4().makeTranslation(-center.x, -center.y, -center.z);
			const mRotate = new THREE.Matrix4().makeRotationZ(radians);
			const mMoveBack = new THREE.Matrix4().makeTranslation(center.x, center.y, center.z);
			//const transform = mMoveOrigin.multiply(mRotate).multiply(mMoveBack);
			const transform = mMoveBack.multiply(mRotate).multiply(mMoveOrigin);

			const rotatedPoints = points.map( point => point.clone().applyMatrix4(transform) );

			this.profileWindow.autoFitEnabled = false;

			for(let i = 0; i < points.length; i++){
				profile.setPosition(i, rotatedPoints[i]);
			}
		}

		$("#potree_profile_rotate_cw").click( () => {
			const radians = THREE.Math.degToRad(this.rotateAmount);
			rotate(-radians);
		});

		$("#potree_profile_rotate_ccw").click( () => {
			const radians = THREE.Math.degToRad(this.rotateAmount);
			rotate(radians);
		});

		$("#potree_profile_move_forward").click( () => {
			const profile = this.profile;
			const points = profile.points;
			const start = points[0];
			const end = points[points.length - 1];

			const dir = end.clone().sub(start).normalize();
			const up = new THREE.Vector3(0, 0, 1);
			const forward = up.cross(dir);
			const move = forward.clone().multiplyScalar(profile.width / 2);

			this.profileWindow.autoFitEnabled = false;

			for(let i = 0; i < points.length; i++){
				profile.setPosition(i, points[i].clone().add(move));
			}
		});

		$("#potree_profile_move_backward").click( () => {
			const profile = this.profile;
			const points = profile.points;
			const start = points[0];
			const end = points[points.length - 1];

			const dir = end.clone().sub(start).normalize();
			const up = new THREE.Vector3(0, 0, 1);
			const forward = up.cross(dir);
			const move = forward.clone().multiplyScalar(-profile.width / 2);

			this.profileWindow.autoFitEnabled = false;

			for(let i = 0; i < points.length; i++){
				profile.setPosition(i, points[i].clone().add(move));
			}
		});
	}

	setProfile (profile) {
		if (this.profile !== profile && this.profileWindow.tools) {
			this.profileWindow.tools.reset();
		}

		// anden profil: markøren i 3D væk og kamerafølgen starter forfra (første gyldige position zoomer igen)
		if (this.profile !== profile) {
			this.profileWindow.clearHover();
			this.profileWindow.hover.reset();
		}

		if (this.profile !== null && this.profile !== profile) {
			this.profile.removeEventListener('marker_moved', this._recompute);
			this.profile.removeEventListener('marker_added', this._recompute);
			this.profile.removeEventListener('marker_removed', this._recompute);
			this.profile.removeEventListener('width_changed', this._recompute);
		}

		this.profile = profile;

		{
			this.profile.addEventListener('marker_moved', this._recompute);
			this.profile.addEventListener('marker_added', this._recompute);
			this.profile.addEventListener('marker_removed', this._recompute);
			this.profile.addEventListener('width_changed', this._recompute);
		}

		this.recompute();
	}

	// Genberegner de ekstra linjer (kan kaldes af alle der aendrer noget de afhaenger af: IFC
	// tændt/slukket/skjult, baggrundskort tændt/slukket). Samles i 150 ms, og en nyere
	// beregning afbryder en igangvaerende.
	// Tænd/sluk for en overlay-kilde ("ifc" = model, "basemap" = DTM) fra profilvinduets flueben
	setOverlayEnabled (id, enabled) {
		this.overlayEnabled[id] = !!enabled;
		this.refreshOverlays();
	}

	// {ifc: {available, enabled}, basemap: {available, enabled}}
	getOverlayState () {
		let state = {};
		for (let source of this.overlaySources) {
			state[source.id] = {available: source.available(), enabled: this.overlayEnabled[source.id] !== false};
		}
		return state;
	}

	refreshOverlays () {
		if (this.overlayTimer !== null) {
			clearTimeout(this.overlayTimer);
		}
		this.overlayTimer = setTimeout(() => {
			this.overlayTimer = null;
			this.computeOverlays();
		}, 150);
	}

	async computeOverlays () {
		let token = ++this.overlayToken;
		let isCurrent = () => token === this.overlayToken;
		this.viewer.dispatchEvent({type: "profile_overlays_updated", state: this.getOverlayState()});

		// ikke gatet paa profileWindow.enabled: PointView viser vinduet med Vue (v-show) uden at
		// kalde show(), saa flaget er aldrig sat dér (punkterne tegnes af samme grund uafhaengigt af det)
		if (!this.profile) {
			return;
		}
		let segments = profileSegments(this.profile.points);
		if (segments.length === 0) {
			this.profileWindow.clearOverlays();
			return;
		}

		for (let source of this.overlaySources) {
			if (this.overlayEnabled[source.id] === false) {
				this.profileWindow.setOverlay(source.id, null);
				continue;
			}
			try {
				let result = await source.compute(segments, isCurrent);
				if (!isCurrent()) {
					return;
				}
				// en kilde leverer enten linjesegmenter, eller {positions, colors} (farve pr. vertex)
				let positions = result && result.positions ? result.positions : result;
				let colors = result && result.colors ? result.colors : null;
				this.profileWindow.setOverlay(source.id, positions, source.color, colors);
			} catch (e) {
				console.warn("Profil-overlay '" + source.id + "' fejlede:", e);
				if (isCurrent()) {
					this.profileWindow.setOverlay(source.id, null);
				}
			}
		}
	}

	reset () {
		this.profileWindow.reset();

		this.numPoints = 0;

		if (this.profile) {
			for (let request of this.requests) {
				request.cancel();
			}
		}
	}

	progressHandler (pointcloud, progress) {
		for (let segment of progress.segments) {
			this.profileWindow.addPoints(pointcloud, segment.points);
			this.numPoints += segment.points.numPoints;
		}
	}

	cancel () {
		for (let request of this.requests) {
			request.cancel();
			// request.finishLevelThenCancel();
		}

		this.requests = [];
	};

	finishLevelThenCancel(){
		for (let request of this.requests) {
			request.finishLevelThenCancel();
		}

		this.requests = [];
	}

	recompute () {
		if (!this.profile) {
			return;
		}

		if (this.scheduledRecomputeTime !== null && this.scheduledRecomputeTime > new Date().getTime()) {
			return;
		} else {
			this.scheduledRecomputeTime = new Date().getTime() + 100;
		}
		this.scheduledRecomputeTime = null;

		this.reset();
		this.refreshOverlays();

		for (let pointcloud of this.viewer.scene.pointclouds.filter(p => p.visible)) {
			let request = pointcloud.getPointsInProfile(this.profile, null, {
				'onProgress': (event) => {
					if (!this.enabled) {
						return;
					}

					this.progressHandler(pointcloud, event.points);

					if (this.numPoints > this.threshold) {
						this.finishLevelThenCancel();
					}
				},
				'onFinish': (event) => {
					if (!this.enabled) {

					}
				},
				'onCancel': () => {
					if (!this.enabled) {

					}
				}
			});

			this.requests.push(request);
		}
	}
};
